import { promiseWrapper } from '../../../assets/scripts/utils/promise'
import cookieService from 'services/cookies'
import { getCurrentLanguage } from './utils/current-language'
import {
  emailValidator,
  emptyValidator,
  passwordValidator,
  checkedValidator,
} from '../../../assets/scripts/services/validators-functions'
import { RegisterPayload, FormRegistrationOptions } from './models/input-form'
import { t9n } from 't9n/t9n'

declare let USER_BASE_URL_IK: string

const $submitFormButton = document.querySelector<HTMLButtonElement>('[data-submit-button]')
const $emailInputElement = document.querySelector<HTMLInputElement>("input[name='email']")
const $passwordInputElement = document.querySelector<HTMLInputElement>("input[name='password']")
const $serviceTermsInputElement = document.querySelector<HTMLInputElement>("input[name='reg-consent']")
const $promoTermsInputElement = document.querySelector<HTMLInputElement>("input[name='prom-consent']")
const $marketingConsentInputElement = document.querySelector<HTMLInputElement>("input[name='marketing-consent']")
const $promocodeInputElement = document.querySelector<HTMLInputElement>("input[name='registration_form']")
const $allErrorSpans = document.querySelectorAll<HTMLElement>('[data-error]')
const $allErrorCheckboxSpans = document.querySelectorAll<HTMLElement>('[data-checkbox-error]')

const getEmailFromForm = (): string => {
  return $emailInputElement.value.trim()
}

const getPasswordFromForm = (): string => {
  return $passwordInputElement.value.trim()
}

const getServiceTermConsentFromForm = (): boolean => {
  return $serviceTermsInputElement.checked
}

const getPromotionTermConsentFromForm = (): boolean => {
  return $promoTermsInputElement.checked
}

const getMarketingConsentFromForm = (): boolean => {
  return $marketingConsentInputElement.checked
}

const getPromocodeValueFromForm = (): string => {
  return $promocodeInputElement.value.trim()
}

const getErrorContent = (errorID: string): string => {
  const currentLanguage = getCurrentLanguage()
  const promocodeExpiredUrl = ' <a href="internetowykantor.pl/?ref=land-promo-expired">internetowykantor.pl</a>'
  switch (errorID) {
    case 'email-mandatory':
    case 'password-mandatory':
      return t9n('landingForm.notEmptyField', currentLanguage)
    case 'email-invalid':
      return t9n('landingForm.incorrectEmail', currentLanguage)
    case 'password-too-short':
      return t9n('landingForm.toShortPassowrd', currentLanguage)
    case 'promocode-invalid':
      return t9n('landingForm.invalidPromocode', currentLanguage) + promocodeExpiredUrl
    case 'email-already-exists':
      return t9n('landingForm.emailAlreadyExists', currentLanguage)
    case 'general-invalid':
      return t9n('landingForm.generalInvalid', currentLanguage)
    default:
      return t9n('landingForm.requiredField', currentLanguage)
  }
}

const showCheckboxError = (errorID: string): void => {
  const $errorElement = document.querySelector<HTMLElement>(`[data-checkbox-error='${errorID}']`)
  if ($errorElement) {
    $errorElement.hidden = false
    $errorElement.innerHTML = getErrorContent(errorID)
  }
}

const showError = ($element: HTMLInputElement, errorID: string): void => {
  if ($element) {
    $element.classList.add('is-with-error')
  }

  const errorDataSelectorName = errorID.split('-')[0]

  const $errorElement = document.querySelector<HTMLElement>(`[data-error='${errorDataSelectorName}']`)
  if ($errorElement) {
    $errorElement.hidden = false
    $errorElement.innerHTML = getErrorContent(errorID)
  }
}

const resetAllErrors = () => {
  $emailInputElement.classList.remove('is-with-error')
  $passwordInputElement.classList.remove('is-with-error')

  for (const $singleErrorSpan of $allErrorSpans) {
    $singleErrorSpan.hidden = true
  }

  for (const $singleErrorCheckboxSpan of $allErrorCheckboxSpans) {
    $singleErrorCheckboxSpan.hidden = true
  }
}

const checkEmailValidation = (): boolean => {
  if (emptyValidator(getEmailFromForm())) {
    showError($emailInputElement, 'email-mandatory')
    return false
  }

  if (emailValidator(getEmailFromForm())) {
    showError($emailInputElement, 'email-invalid')
    return false
  }

  return true
}

const checkPasswordValidation = (): boolean => {
  if (emptyValidator(getPasswordFromForm())) {
    showError($passwordInputElement, 'password-mandatory')
    return false
  }

  if (passwordValidator(getPasswordFromForm())) {
    showError($passwordInputElement, 'password-too-short')
    return false
  }

  return true
}

const checkTermsOfServiceValidation = (): boolean => {
  if (checkedValidator(getServiceTermConsentFromForm())) {
    showCheckboxError('reg-consent-mandatory')
    return false
  }

  return true
}

const checkPromotionTremsValidation = (): boolean => {
  if (checkedValidator(getPromotionTermConsentFromForm())) {
    showCheckboxError('prom-consent-mandatory')
    return false
  }

  return true
}

const isFormComplete = (options: FormRegistrationOptions): boolean => {
  return [
    checkEmailValidation(),
    checkPasswordValidation(),
    checkTermsOfServiceValidation(),
    options.withoutPromotionTerms ? true : checkPromotionTremsValidation(),
  ].every((v) => v)
}

const handleFormSubmit = (options: FormRegistrationOptions): void => {
  resetAllErrors()
  const language = getCurrentLanguage()

  if (isFormComplete(options)) {
    promiseWrapper<string, undefined>(`${USER_BASE_URL_IK}/api/public/csrf-token`, 'GET', undefined, {
      withCredentials: true,
      contentType: 'text/plain',
    })
      .then((res) => {
        const payload: RegisterPayload = {
          email: getEmailFromForm(),
          password: getPasswordFromForm(),
          terms_and_conditions_accepted: getServiceTermConsentFromForm(),
          marketing_email_actions_accepted: getMarketingConsentFromForm() || undefined,
          promocode: getPromocodeValueFromForm(),
          region: 'PL',
          _csrf: res,
          language,
        }

        return promiseWrapper<null, RegisterPayload>(`${USER_BASE_URL_IK}/api/public/registration`, 'POST', payload, {
          withCredentials: true,
        })
      })
      .then(() => {
        cookieService.setItem('confirm_email', getEmailFromForm(), 'Lax', 1)
        window.location.href = `${USER_BASE_URL_IK}/?lang=${language}#/confirm-email`
      })
      .catch((e) => {
        const JSONResponse = JSON.parse(e.responseText)

        if (JSONResponse.error) {
          if (JSONResponse.error === 'promocode-invalid') {
            showError(null, JSONResponse.error)
          } else if (JSONResponse.error === 'email-already-exists') {
            showError($emailInputElement, JSONResponse.error)
          } else {
            showError(null, 'general-invalid')
          }
        }
      })
  }
}

function paramInjector(fn, ...args) {
  return function (...otherArgs) {
    return fn.apply(this, [...args, ...otherArgs])
  }
}

export function handleForm(options: FormRegistrationOptions = { withoutPromotionTerms: false }): void {
  $submitFormButton.addEventListener('click', paramInjector(handleFormSubmit, options))
}
